
import React from 'react';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { setupRlsPolicies } from '@/integrations/supabase/rlsPolicies';
import { toast } from '@/components/ui/sonner';
import { SUPABASE_URL } from '@/utils/supabaseConfig';

interface CourierConnectionErrorProps {
  debugInfo: any;
  onRetryConnection: () => void;
  onInitializeDatabase: () => void;
}

export const CourierConnectionError: React.FC<CourierConnectionErrorProps> = ({
  debugInfo,
  onRetryConnection,
  onInitializeDatabase
}) => {
  const handleSetupRlsPolicies = async () => {
    try {
      toast.info('Настраиваем RLS политики для таблицы couriers...');
      const result = await setupRlsPolicies('couriers');
      
      if (result.success) {
        toast.success(result.message);
        onRetryConnection(); // Повторно проверяем подключение после настройки политик
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Ошибка при настройке RLS политик:', error);
      toast.error('Не удалось настроить RLS политики');
    }
  };

  // Получаем project ID из URL для ссылки на панель Supabase
  const getProjectId = () => {
    try {
      const url = new URL(SUPABASE_URL);
      const hostname = url.hostname;
      // Для стандартных supabase URL: project-id.supabase.co
      if (hostname.includes('.supabase.co')) {
        return hostname.split('.')[0];
      }
      // Для кастомных доменов возвращаем hostname
      return hostname;
    } catch {
      return 'unknown';
    }
  };

  const getDashboardUrl = () => {
    const projectId = getProjectId();
    // Для стандартных supabase доменов строим dashboard URL
    if (SUPABASE_URL.includes('.supabase.co')) {
      return `https://supabase.com/dashboard/project/${projectId}`;
    }
    // Для кастомных доменов открываем сам URL
    return SUPABASE_URL;
  };

  return (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>Ошибка подключения к Supabase</AlertTitle>
      <AlertDescription className="space-y-4">
        <div>
          {debugInfo?.errorType === 'rlsError' ? (
            <>
              Не удалось получить данные из-за ошибки RLS политик. 
              Необходимо проверить политику чтения для таблицы couriers.
            </>
          ) : (
            <>
              Не удалось подключиться к базе данных Supabase. Проверьте настройки подключения и права доступа.
              Временно используются тестовые данные.
            </>
          )}
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onRetryConnection}
          >
            Повторить подключение
          </Button>
          
          <Button 
            variant="outline" 
            size="sm"
            onClick={onInitializeDatabase}
          >
            Инициализировать БД
          </Button>
          
          {debugInfo?.errorType === 'rlsError' && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleSetupRlsPolicies}
            >
              Настроить RLS политики
            </Button>
          )}
          
          <Button 
            variant="destructive" 
            size="sm"
            onClick={() => window.open(getDashboardUrl(), '_blank')}
          >
            Открыть панель Supabase
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
};
